import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Link, Paper, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { CreditNoteService } from '../../services/creditNote.service';
import { SalesInvoiceService } from '../../services/salesInvoice.service';
import { AccountService } from '../../services/account.service';
import { AllocationService } from '../../services/allocation.service';
import { Allocation } from '../../interfaces/allocation.interface';
import { CreditNote, CreditNoteRefund } from '../../interfaces/sales/creditNote.interface';
import { Account } from '../../interfaces/accounting.interface';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import ScrollableTable from '../common/ScrollableTable';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import AllocateDialog, { AllocationTargetOption } from '../allocation/AllocateDialog';
import AllocationsPanel from '../allocation/AllocationsPanel';
import { depositAccountOptions } from '../../utils/postingAccounts';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { formatDate, toLocalIsoDate } from '../../utils/dateUtils';
import { saveBlob } from '../../utils/saveBlob';
import { invoiceBalanceDue } from './documentLabels';
import CreditNoteGatewayRefund from './CreditNoteGatewayRefund'; // P1-B

const CreditNoteDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const canManage = usePermission(PERMISSIONS.SALES_INVOICE_MANAGE);
  const canAllocate = usePermission(PERMISSIONS.RECEIPT_MANAGE);

  const [note, setNote] = useState<CreditNote | null>(null);
  const [refunds, setRefunds] = useState<CreditNoteRefund[]>([]);
  const [originApplied, setOriginApplied] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [allocRefresh, setAllocRefresh] = useState(0);

  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [applyOpen, setApplyOpen] = useState(false);
  const [targets, setTargets] = useState<AllocationTargetOption[]>([]);
  const [refundOpen, setRefundOpen] = useState(false);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [refundAmount, setRefundAmount] = useState<number | ''>('');
  const [refundAccount, setRefundAccount] = useState('');
  const [refundMethod, setRefundMethod] = useState('');
  const [refundRef, setRefundRef] = useState('');

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const n = await CreditNoteService.getById(id);
      setNote(n);
      setRefunds(await CreditNoteService.listRefunds(id));
      // The credit auto-applies to its own invoice when it is open; show that apart from "other invoices".
      try {
        const rows: Allocation[] = await AllocationService.forCredit('SALES_CREDIT_NOTE', id);
        setOriginApplied((Array.isArray(rows) ? rows : [])
          .filter(a => a.status === 'ACTIVE' && a.targetId === n.salesInvoiceId)
          .reduce((sum, a) => sum + Number(a.amount), 0));
      } catch {
        setOriginApplied(0);
      }
    } catch (e) {
      toast.showApiError(e, 'Failed to load credit note');
    } finally {
      setLoading(false);
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const openApply = async () => {
    if (!note) return;
    try {
      const list = await SalesInvoiceService.listByCustomer(note.customerId);
      setTargets(list
        .filter(i => (i.status === 'ISSUED' || i.status === 'PARTIALLY_PAID') && i.id !== note.salesInvoiceId)
        .map(i => ({ id: i.id, label: i.invoiceNumber ?? 'Draft', outstanding: invoiceBalanceDue(i) }))
        .filter(t => t.outstanding > 0.004));
      setApplyOpen(true);
    } catch (e) {
      toast.showApiError(e, 'Failed to load invoices');
    }
  };

  const openRefund = async () => {
    if (!note) return;
    try {
      const a = await AccountService.getAccountsByType('ASSET');
      setAccounts(depositAccountOptions(a));
    } catch (e) {
      toast.showApiError(e, 'Failed to load accounts');
    }
    setRefundAmount(note.unappliedAmount);
    setRefundAccount(''); setRefundMethod(''); setRefundRef('');
    setRefundOpen(true);
  };

  const run = async (fn: () => Promise<unknown>, ok: string, fail: string) => {
    setBusy(true);
    try {
      await fn();
      toast.showSuccess(ok);
      await load();
      setAllocRefresh(k => k + 1);
    } catch (e) {
      toast.showApiError(e, fail);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  if (!note) return <Box sx={{ p: 3 }}><Typography>Credit note not found.</Typography></Box>;

  const posted = note.status === 'POSTED';
  const refundTooBig = refundAmount !== '' && Number(refundAmount) > note.unappliedAmount + 0.005;

  return (
    <Box sx={{ p: 3 }} data-help-page="sales.creditNote.page">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">{note.creditNoteNumber}</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {canAllocate && posted && note.unappliedAmount > 0.004 && (
            <Button data-help="sales.creditNote.apply" variant="contained" onClick={openApply}>Apply to invoice</Button>
          )}
          {canManage && posted && note.unappliedAmount > 0.004 && (
            <Button data-help="sales.creditNote.refund" variant="outlined" onClick={openRefund}>Refund</Button>
          )}
          <Button data-help="sales.creditNote.pdf" variant="outlined" disabled={busy} onClick={async () => {
            try { saveBlob(await CreditNoteService.downloadPdf(note.id), `${note.creditNoteNumber}.pdf`); }
            catch (e) { toast.showApiError(e, 'Failed to download PDF'); }
          }}>Mushak 6.7 PDF</Button>
          {canManage && posted && (
            <Button data-help="sales.creditNote.email" variant="outlined" disabled={busy}
              onClick={() => run(() => CreditNoteService.email(note.id), 'Credit note emailed', 'Failed to email credit note')}>
              Email
            </Button>
          )}
          {canManage && posted && (
            <Button data-help="sales.creditNote.void" variant="outlined" color="error" onClick={() => { setVoidReason(''); setVoidOpen(true); }}>Void</Button>
          )}
          <Button data-help="sales.common.back" variant="outlined" onClick={() => navigate(-1)}>Back</Button>
        </Box>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">Status</Typography>
            <Chip size="small" label={humanizeEnum(note.status)} color={posted ? 'success' : 'default'} />
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Invoice</Typography>
            <Link component={RouterLink} to={`/sales/invoices/${note.salesInvoiceId}`}>{note.invoiceNumber ?? 'View invoice'}</Link>
          </Box>
          <Box><Typography variant="body2" color="text.secondary">Customer</Typography><Typography>{note.customerName ?? '—'}</Typography></Box>
          <Box><Typography variant="body2" color="text.secondary">Date</Typography><Typography>{formatDate(note.noteDate)}</Typography></Box>
          <Box><Typography variant="body2" color="text.secondary">Subtotal</Typography><Typography>{formatMoney(note.subtotal)}</Typography></Box>
          <Box><Typography variant="body2" color="text.secondary">Tax</Typography><Typography>{formatMoney(note.taxAmount)}</Typography></Box>
          <Box><Typography variant="body2" color="text.secondary">Total</Typography><Typography fontWeight="bold">{formatMoney(note.totalAmount)}</Typography></Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Applied to {note.invoiceNumber ?? 'its invoice'}</Typography>
            <Typography>{formatMoney(originApplied)}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">Applied to other invoices</Typography>
            <Typography>{formatMoney(Math.max(0, note.allocatedAmount - originApplied))}</Typography>
          </Box>
          <Box><Typography variant="body2" color="text.secondary">Refunded</Typography><Typography>{formatMoney(note.refundedAmount)}</Typography></Box>
          <Box><Typography variant="body2" color="text.secondary">Unapplied</Typography><Typography fontWeight="bold">{formatMoney(note.unappliedAmount)}</Typography></Box>
          <Box sx={{ gridColumn: '1 / -1' }}>
            <Typography variant="body2" color="text.secondary">Reason</Typography>
            <Typography>{note.reason}</Typography>
          </Box>
          {note.status === 'VOIDED' && (
            <Box sx={{ gridColumn: '1 / -1' }}>
              <Typography variant="body2" color="text.secondary">Voided {note.voidedDate ? formatDate(note.voidedDate) : ''}</Typography>
              <Typography>{note.voidReason}</Typography>
            </Box>
          )}
          {note.journalEntryId && (
            <Box>
              <Typography variant="body2" color="text.secondary">Journal Entry</Typography>
              <Link component={RouterLink} to={`/accounting/journal-entries/${note.journalEntryId}`}>View JE</Link>
            </Box>
          )}
        </Box>
      </Paper>

      <Paper sx={{ p: 2 }} data-help-section="sales.creditNote.lines">
        <Typography variant="h6" sx={{ mb: 1 }}>Lines</Typography>
        <ScrollableTable>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell data-help="sales.creditNote.lines.col.description">Description</TableCell>
                <TableCell align="right" data-help="sales.creditNote.lines.col.net">Net</TableCell>
                <TableCell align="right" data-help="sales.creditNote.lines.col.tax">Tax</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {note.lines.map(l => (
                <TableRow key={l.id}>
                  <TableCell>{l.description ?? '—'}</TableCell>
                  <TableCell align="right">{formatMoney(l.netAmount)}</TableCell>
                  <TableCell align="right">{formatMoney(l.taxAmount)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </ScrollableTable>
      </Paper>

      {refunds.length > 0 && (
        <Paper sx={{ p: 2, mt: 3 }} data-help-section="sales.creditNote.refunds">
          <Typography variant="h6" sx={{ mb: 1 }}>Refunds</Typography>
          <ScrollableTable>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell data-help="sales.creditNote.refunds.col.number">Refund</TableCell><TableCell data-help="sales.creditNote.refunds.col.date">Date</TableCell><TableCell align="right" data-help="sales.creditNote.refunds.col.amount">Amount</TableCell>
                  <TableCell data-help="sales.creditNote.refunds.col.method">Method</TableCell><TableCell data-help="sales.creditNote.refunds.col.status">Status</TableCell><TableCell data-help="sales.creditNote.refunds.col.actions" />
                </TableRow>
              </TableHead>
              <TableBody>
                {refunds.map(r => (
                  <TableRow key={r.id}>
                    <TableCell>{r.refundNumber}</TableCell>
                    <TableCell>{formatDate(r.refundDate)}</TableCell>
                    <TableCell align="right">{formatMoney(r.amount)}</TableCell>
                    <TableCell>{r.method ? humanizeEnum(r.method) : '—'}</TableCell>
                    <TableCell>{humanizeEnum(r.status)}</TableCell>
                    <TableCell align="right">
                      {canManage && r.status === 'POSTED' && (
                        <Button data-help="sales.creditNote.refunds.reverse" size="small" color="error" disabled={busy}
                          onClick={() => run(() => CreditNoteService.reverseRefund(r.id), 'Refund reversed', 'Failed to reverse refund')}>
                          Reverse
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollableTable>
        </Paper>
      )}

      {/* ---- P1-B gateway refunds ---- */}
      {posted && <CreditNoteGatewayRefund noteId={note.id} onChanged={load} />}
      {/* ---- end P1-B ---- */}

      <AllocationsPanel creditType="SALES_CREDIT_NOTE" creditId={note.id} refreshKey={allocRefresh}
        canReverse={canAllocate && posted} onChanged={load} />

      <AllocateDialog
        open={applyOpen}
        onClose={() => setApplyOpen(false)}
        onAllocated={() => { load(); setAllocRefresh(k => k + 1); }}
        creditType="SALES_CREDIT_NOTE"
        creditId={note.id}
        available={note.unappliedAmount}
        targetType="SALES_INVOICE"
        targets={targets}
        title="Apply credit note to invoice"
        targetLabel="Open invoice"
      />

      <Dialog open={voidOpen} onClose={() => !busy && setVoidOpen(false)} maxWidth="sm" fullWidth data-help-page="sales.creditNote.voidDialog.page">
        <DialogTitle>Void credit note</DialogTitle>
        <DialogContent data-help-section="sales.creditNote.voidDialog.section.reason">
          <Typography variant="body2" sx={{ mb: 2 }}>
            Posts the exact mirror of the credit note, restores the invoice balance and reverses its tax rows.
            A note with active allocations or refunds cannot be voided: reverse those first. A note you issued yourself must be voided by a second person.
          </Typography>
          <TextField data-help="sales.creditNote.voidDialog.reason" label="Reason" required fullWidth multiline minRows={2} value={voidReason}
            onChange={e => setVoidReason(e.target.value)} inputProps={{ maxLength: 1000 }} />
        </DialogContent>
        <DialogActions>
          <Button data-help="sales.creditNote.voidDialog.cancel" onClick={() => setVoidOpen(false)} disabled={busy}>Cancel</Button>
          <Button data-help="sales.creditNote.void" color="error" variant="contained" disabled={busy || !voidReason.trim()}
            onClick={async () => {
              await run(() => CreditNoteService.voidNote(note.id, voidReason.trim()), 'Credit note voided', 'Failed to void credit note');
              setVoidOpen(false);
            }}>
            Void
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={refundOpen} onClose={() => !busy && setRefundOpen(false)} maxWidth="sm" fullWidth data-help-page="sales.creditNote.refundDialog.page">
        <DialogTitle>Refund to customer</DialogTitle>
        <DialogContent data-help-section="sales.creditNote.refundDialog.section.form">
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2">Unapplied credit: {formatMoney(note.unappliedAmount)}</Typography>
            <TextField data-help="sales.creditNote.refundDialog.amount" label="Amount" type="number" required fullWidth value={refundAmount} error={refundTooBig}
              helperText={refundTooBig ? 'More than the unapplied credit' : undefined}
              onChange={e => setRefundAmount(e.target.value === '' ? '' : Number(e.target.value))}
              inputProps={{ min: 0, step: '0.01' }} />
            <AccountAutocomplete data-help="sales.creditNote.refundDialog.account" accounts={accounts} value={refundAccount} onChange={setRefundAccount}
              label="Paid from account" required />
            <TextField data-help="sales.creditNote.refundDialog.method" label="Method" fullWidth value={refundMethod} onChange={e => setRefundMethod(e.target.value)}
              inputProps={{ maxLength: 20 }} />
            <TextField data-help="sales.creditNote.refundDialog.reference" label="Reference" fullWidth value={refundRef} onChange={e => setRefundRef(e.target.value)}
              inputProps={{ maxLength: 255 }} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button data-help="sales.creditNote.refundDialog.cancel" onClick={() => setRefundOpen(false)} disabled={busy}>Cancel</Button>
          <Button data-help="sales.creditNote.refund" variant="contained" disabled={busy || !refundAccount || refundAmount === '' || Number(refundAmount) <= 0 || refundTooBig}
            onClick={async () => {
              await run(() => CreditNoteService.refund(note.id, {
                refundDate: toLocalIsoDate(), amount: Number(refundAmount), paymentAccountId: refundAccount,
                method: refundMethod.trim() || undefined, reference: refundRef.trim() || undefined,
              }), 'Refund recorded', 'Failed to record refund');
              setRefundOpen(false);
            }}>
            Refund
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CreditNoteDetail;
